/**
 * G2eGente - Seleção de Vaga para Relatório Comparativo
 * Menu: Relatório Comparativo -> Seleciona Vaga -> Visualiza Comparativo
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  BarChart3,
  Briefcase,
  Search,
  Loader2,
  MapPin,
  Users,
  Target,
  ChevronRight,
  AlertCircle,
  Brain,
  CheckCircle
} from 'lucide-react';

// DISC colors
const DISC_COLORS = {
  'D': 'bg-red-100 text-red-700 border-red-300',
  'I': 'bg-yellow-100 text-yellow-700 border-yellow-300',
  'S': 'bg-green-100 text-green-700 border-green-300',
  'C': 'bg-blue-100 text-blue-700 border-blue-300'
};

export default function ComparisonSelector() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.getMy();
      setJobs(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar vagas');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.localizacao?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const jobsWithProfile = filteredJobs.filter(j => j.perfil_ideal?.disc_desejado || j.perfil_ideal?.niveis?.length > 0);
  const jobsWithoutProfile = filteredJobs.filter(j => !j.perfil_ideal?.disc_desejado && !j.perfil_ideal?.niveis?.length);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'em_andamento': { label: 'Em Andamento', className: 'bg-green-100 text-green-700' },
      'pausada': { label: 'Pausada', className: 'bg-amber-100 text-amber-700' },
      'finalizada': { label: 'Finalizada', className: 'bg-slate-100 text-slate-700' },
      'cancelada': { label: 'Cancelada', className: 'bg-red-100 text-red-700' }
    };
    const config = statusConfig[status] || statusConfig['em_andamento'];
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="comparison-selector">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-cyan-600" />
              </div>
              Relatório Comparativo
            </h1>
            <p className="text-slate-600 mt-1">
              Selecione uma vaga para visualizar o comparativo entre o perfil ideal e os candidatos
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar vaga por título ou localização..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : jobs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-900">Nenhuma vaga cadastrada</h3>
              <p className="text-sm text-slate-600 mt-2">
                Crie uma vaga primeiro para usar o relatório comparativo.
              </p>
              <Button 
                onClick={() => navigate('/company/jobs/create')}
                className="mt-4 bg-cyan-600 hover:bg-cyan-700"
              >
                Criar Vaga
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Jobs with Profile Configured */}
            {jobsWithProfile.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-slate-900">
                    Vagas com Perfil Ideal Configurado ({jobsWithProfile.length})
                  </h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {jobsWithProfile.map((job) => (
                    <Card 
                      key={job.id} 
                      className="border-slate-200 hover:border-cyan-300 hover:shadow-md transition-all cursor-pointer group"
                      onClick={() => navigate(`/company/jobs/${job.id}/comparison`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate group-hover:text-cyan-700 transition-colors">
                              {job.titulo}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{job.localizacao || 'Não informado'}</span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 transition-colors flex-shrink-0" />
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {getStatusBadge(job.status)}
                          {job.perfil_ideal?.disc_desejado && (
                            <Badge className={`${DISC_COLORS[job.perfil_ideal.disc_desejado[0]] || 'bg-slate-100 text-slate-700'}`}>
                              <Brain className="w-3 h-3 mr-1" />
                              DISC: {job.perfil_ideal.disc_desejado}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{job.total_candidatos || 0} candidatos</span>
                          </div>
                          {job.perfil_ideal?.niveis?.length > 0 && (
                            <div className="flex items-center gap-1 text-cyan-600">
                              <Target className="w-4 h-4" />
                              <span>{job.perfil_ideal.niveis.length} níveis</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Jobs without Profile */}
            {jobsWithoutProfile.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-slate-900">
                    Vagas sem Perfil Ideal ({jobsWithoutProfile.length})
                  </h2>
                </div>
                
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                  <p className="text-sm text-amber-700">
                    Configure o perfil ideal nestas vagas para usar o relatório comparativo e ranking de candidatos.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {jobsWithoutProfile.map((job) => (
                    <Card 
                      key={job.id} 
                      className="border-slate-200 border-dashed opacity-75 hover:opacity-100 transition-opacity"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-700 truncate">
                              {job.titulo}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{job.localizacao || 'Não informado'}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {getStatusBadge(job.status)}
                          <Badge className="bg-amber-100 text-amber-700 border-amber-400">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Sem perfil
                          </Badge>
                        </div>
                        
                        <Button 
                          size="sm"
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/company/jobs/${job.id}/edit`);
                          }}
                        >
                          <Target className="w-4 h-4 mr-2" />
                          Configurar Perfil Ideal
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
